import { Injectable } from '@angular/core';

import { SpecificationFormRule } from '../project-type-rules/specification-form-rule';
import { ExternalPrefillProvider } from './external-prefill-provider.model';
import { RuleResolutionContext, WorkflowProgramRef } from './project-creation-workflow.model';
import { baseRuleFor, findBrokenCouplings, ResolveRuleResult, resolveRule } from './resolve-rule';
import { RegisteredWorkflow, WorkflowRegistryService } from './workflow-registry.service';

/**
 * Angular-facing entry point for project-creation rule resolution.
 *
 * A thin delegate by design: all the logic worth testing lives in the Angular-free
 * `resolve-rule.ts`, so it runs under `npm run test:pure` in ~0.1s. This class only supplies the
 * registry and exposes the provider binding.
 */
@Injectable({ providedIn: 'root' })
export class ProjectCreationWorkflowService {

  constructor(private readonly registry: WorkflowRegistryService) {}

  /**
   * Resolves the field rule for a form.
   *
   * @param projectType the project type, or null when not yet known (a `providesProjectType`
   *   workflow before confirm) — resolution then returns `STAGE_ONE_RULE`, never null
   * @param program the AUTHORITATIVE program object. Four sources differ across the entry points;
   *   pass the working `Project`'s / context's program, not the dropdown list's copy, and note
   *   `ProgramApplication.program` strips `eligibleProjectTypes`.
   */
  resolve(
    projectType: string | null | undefined,
    program: WorkflowProgramRef | null | undefined,
    context?: RuleResolutionContext
  ): ResolveRuleResult {
    return resolveRule(projectType, program, context ?? {}, this.registry.all());
  }

  /**
   * Options for the SYSTEM_ADMIN config dropdown (FM8). `null` is "Default" — no workflow — and is
   * always offered, so a configured program can always be returned to default behavior. That is
   * the documented production off-switch, so it must never be conditional on the registry.
   */
  workflowOptions(): { value: string | null; label: string }[] {
    return this.registry.options();
  }

  /** The workflow a program names, if it is registered. */
  workflowFor(program: WorkflowProgramRef | null | undefined): RegisteredWorkflow | undefined {
    return this.registry.byKey(program?.projectCreationWorkflowKey);
  }

  /** True when the project type will arrive from a provider rather than the entry point. */
  providesProjectType(program: WorkflowProgramRef | null | undefined): boolean {
    return !!this.workflowFor(program)?.providesProjectType;
  }

  /**
   * The provider class for a program's workflow, for the caller to inject. Undefined when the
   * program has no workflow or the workflow has no provider — which is every program today.
   */
  prefillProviderTypeFor(program: WorkflowProgramRef | null | undefined) {
    return this.workflowFor(program)?.prefillProvider as
      (undefined | (new (...args: unknown[]) => ExternalPrefillProvider));
  }

  /**
   * Fields the provider owns, as a RENDERING HINT ONLY (design D1). The server derives real
   * ownership from the stored patch; never treat this list as an authorization decision.
   */
  providerOwnedFields(program: WorkflowProgramRef | null | undefined): string[] {
    return (this.workflowFor(program)?.providerOwnedFields ?? []) as string[];
  }

  /**
   * Development guard: reports coupled field pairs an overlay newly desynchronized relative to the
   * base rule. Logged rather than thrown, because a mis-declared overlay should not take the form
   * down — but it must not pass silently either, since the cascade call sites assume the pairing.
   */
  warnOnBrokenCouplings(projectType: string | null | undefined, rule: SpecificationFormRule): void {
    if (!projectType) {
      return;
    }
    const base = baseRuleFor(projectType);
    if (!base) {
      return;
    }
    const broken = findBrokenCouplings(rule, base);
    if (broken.length) {
      // eslint-disable-next-line no-console
      console.warn(
        '[project-creation] Workflow overlay desynchronized coupled field pair(s):',
        broken.map(pair => pair.join(' / ')).join(', ')
      );
    }
  }
}
